/**
 * Provides APIs to insert/update/delete/reorder for ListActions.
 */
/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import Utils from '../utils/utils';
import * as StateManager from '../store/state/state-manager';
import { listAction } from '../store/state/actions/action-creators';
import Constants, { publicConstants } from '../constants';
import { getNormalizedPropertyName } from '../utils/form-handler';
import FieldUtils from '../fields/index';
import { clearMessages } from '../messages';
import type { C11nEnv } from '../interpreter/c11n-env';
import type { ComponentMetadataConfig } from '../interpreter/types';
import type { Action } from '../store/state/state-manager';
import treemanager from '../contextree/treemanager';

import StateMachine from './state-machine/state-machine';
import StateMachineManager from './state-machine-manager';
import type { ListActions, GenericObject } from './types';

const instructions: { [key: string]: any } = {};
const pageInstructionReferences: { [key: string]: any } = {};
const distinctReferenceList: { [key: string]: any } = {};

/**
 * This function helps in creating the context entry in instructions if it not exists before.
 * @param c11nEnv - pConnect object for the list.
 * @param target - target for which the page instruction to be stored
 */
export const createContextEntry = (contextName: string, target: string, pageReference: string) => {
  if (!instructions[contextName]) {
    instructions[contextName] = {};
  }
  if (!instructions[contextName][target]) {
    instructions[contextName][target] = { content: [] };
  }

  if (!pageInstructionReferences[contextName]) {
    pageInstructionReferences[contextName] = [];
    distinctReferenceList[contextName] = [];
  }
  if (!StateMachineManager.isStateMachineExist(contextName, pageReference, target)) {
    StateMachineManager.setStateMachine(
      contextName,
      target,
      pageReference,
      new StateMachine(target, pageReference, contextName)
    );
  }
};

/**
 * This function helps in modifying target in nested page list
 * Example referenceList = ".Questionsets[0].Questions" return = ".Questionsets(1).Questions"
 * @param referenceList - reference string showing the path of target property
 * @returns updated referenceList in case of nested page list
 * @private
 */
const handleTargetInNestedPageList = (referenceList: string): string => {
  return FieldUtils.formatPageReference(referenceList);
};

export const addPageInstructionReferences = (contextName: string, reference: string) => {
  const currentPageInstructionReferences = pageInstructionReferences[contextName];
  const currentDistinctReferenceLists = distinctReferenceList[contextName];

  const ref = getNormalizedPropertyName(reference);
  const distinctRef = ref?.substring(0, ref.indexOf('['));

  if (currentDistinctReferenceLists?.indexOf(distinctRef) < 0) {
    distinctReferenceList[contextName].push(distinctRef);
  }
  if (currentPageInstructionReferences?.indexOf(ref) < 0) {
    pageInstructionReferences[contextName].push(ref);
  }
};

/**
 * This function helps in retrieving the reference lists for the context passed
 * @param contextName - name of the context- e.g. app/primary_1
 * @returns page instructions
 * @private
 */
export const getPageInstructionReferences = (contextName: string) => {
  return pageInstructionReferences[contextName] || [];
};

/**
 * This function helps in clearing the reference lists for the context passed
 * @param contextName - name of the context- e.g. app/primary_1
 * @private
 */
export const clearPageInstructionReferences = (contextName: string) => {
  delete pageInstructionReferences[contextName];
};

/**
 * This function sets isVisible flag for target related  pageInstructions
 * @param c11nEnv - pConnect object for the list
 * @param isVisible - isVisible flag
 * @private
 */
export const setVisibility = (c11nEnv: C11nEnv, isVisible = true) => {
  const contextName = c11nEnv.getContextName();
  const { selectionMode, selectionList, renderMode, referenceList } = c11nEnv.getComponentConfig();
  let target;
  // usecase for multiselect
  if (selectionMode === Constants.LIST_SELECTION_MODE.MULTI && selectionList) {
    target = selectionList;
  } else if (renderMode === 'Editable' && referenceList) {
    // usecase for Editable list and fieldgroup
    target = c11nEnv.getStateProps().referenceList;
  }
  if (target) {
    target = handleTargetInNestedPageList(target);
    const stateMachine = StateMachineManager.getStateMachine(contextName, '', `${c11nEnv.getPageReference()}${target}`);
    if (stateMachine) stateMachine.visibility = isVisible;
  }
};

/**
 * This function helps in retrieving the instruction for the context passed
 * @param contextName - name of the context- e.g. app/primary_1
 * @returns page instructions
 * @private
 */
export const getInstructions = (contextName: string) => {
  return StateMachineManager.buildPageInstructionsByContext(contextName);
};

/**
 * This function helps in retrieving the changed instruction for the context passed
 * @param contextName - name of the context- e.g. app/primary_1
 * @returns page instructions
 * @private
 */
export const getChangedInstructions = (contextName: string) => {
  return StateMachineManager.buildPageInstructionsByContext(contextName, true);
};

/**
 * This function helps in clearing the instruction for the context passed
 * @param contextName - name of the context- e.g. app/primary_1
 * @private
 */
export const clearInstructions = (contextName: string) => {
  StateMachineManager.clearStateMachines(contextName);
  delete instructions[contextName];
};

/**
 * This API helps to add the page context
 * @private
 */
const addCorrectPageContext = (
  referenceList: string,
  pageReference: string,
  payload: Action['payload'],
  index: number
) => {
  let [, nestedProperty] = pageReference.split(`${referenceList}[${index}]`);
  if (nestedProperty !== '' && nestedProperty !== undefined) {
    nestedProperty = nestedProperty.startsWith('.') ? nestedProperty.substring(1) : nestedProperty;
    Object.keys(payload).forEach((key) => {
      payload[`${nestedProperty}.${key}`] = payload[key];
      delete payload[key];
    });
  }
};

/**
 * This function helps in adding INSERT page instruction for referenced list in context
 * @param payload - property values e.g. { prop1: "value", prop2: "value-2"  }
 * @param index - list index of the table row.
 * @param c11nEnv - pConnect object for the list
 * @param pageRef - pageReference
 * @param options - options
 * @private
 */
const insert = (
  payload: object,
  index: number,
  c11nEnv: C11nEnv,
  pageRef?: string | undefined,
  options?: { skipStateUpdate?: boolean; insertIfEmpty?: boolean }
) => {
  const { skipStateUpdate = false, insertIfEmpty = false } = options || {};
  const contextName = c11nEnv.getContextName();
  const referenceList = c11nEnv.getReferenceList();
  const pageReference = pageRef || c11nEnv.getPageReference();
  const target = handleTargetInNestedPageList(referenceList);
  createContextEntry(contextName, target, pageReference);
  addPageInstructionReferences(contextName, `${referenceList}[${index}]`);

  addCorrectPageContext(referenceList, pageReference, payload, index);
  const actionPayload = {
    instruction: Constants.LIST_ACTIONS.INSERT,
    content: {
      ...Utils.unflatten(payload)
    }
  };
  const formSubmitContent = JSON.parse(JSON.stringify(actionPayload)); // cloning the JSON
  delete formSubmitContent.content.classID;

  const stateMachine = StateMachineManager.getStateMachine(contextName, pageReference, target);
  if (insertIfEmpty && stateMachine?.root) {
    return;
  }
  stateMachine.insert(payload, index + 1, skipStateUpdate, { c11nEnv });
};

/**
 * This returns the reference property name of the list based on the pageReference.
 * @param pageReference - fully qualified name of the table
 * @param index - row index of the table
 * @returns returns the reference property name of the table
 */
const getReferenceListFromPageReference = (pageReference: string, index: number) => {
  const startIndex = pageReference.indexOf(publicConstants.CONTENT) + publicConstants.CONTENT.length;
  const endIndex = pageReference.lastIndexOf(`[${index}]`);
  return pageReference.substring(startIndex, endIndex);
};

/**
 * This function helps in adding UPDATE page instruction for referenced list in context
 * @param payload - property values e.g. { prop1: "value", prop2: "value-2"  }
 * @param c11nEnv - pConnect object for the list
 * @param index - list index
 * @param options - options
 * @private
 */
const update = (payload: object, c11nEnv: C11nEnv, index = 0, options: { [key: string]: any } = {}) => {
  const { skipStateUpdate = true, pageRef } = options;
  const contextName = c11nEnv.getContextName();
  let referenceList = c11nEnv.getReferenceList();
  const pageReference = pageRef || c11nEnv.getPageReference();

  if (!referenceList) {
    referenceList = getReferenceListFromPageReference(pageReference, index);
  }
  const target = handleTargetInNestedPageList(referenceList);

  createContextEntry(contextName, target, pageReference);
  addPageInstructionReferences(contextName, `${referenceList}[${index}]`);

  addCorrectPageContext(referenceList, pageReference, payload, index);

  const stateMachine = StateMachineManager.getStateMachine(contextName, pageReference, target);
  stateMachine.update(payload, index + 1, skipStateUpdate, options);
};

/**
 * This updates the value of the property present inside the table
 * @param propertyName - name of the property whose value has to be set.
 * @param value - value of the property whose value has to be set.
 * @param c11nEnv - pConnect object for the table.
 * @private
 */
const updateProperty = (
  propertyName: string | undefined,
  value: GenericObject,
  c11nEnv: C11nEnv,
  options: {
    skipStateUpdate?: boolean;
    isArrayDeepMerge?: boolean;
    skipDirtyValidation?: boolean;
  } = {}
) => {
  const pageReference = c11nEnv.getPageReference();

  const { stateMachineTarget } = StateMachineManager.getStateMachineObjByPath(
    `${pageReference}${propertyName}`,
    c11nEnv.getContextName(),
    `${pageReference}${propertyName}`.endsWith(']')
  );
  if (!stateMachineTarget) {
    // Determine the index of the last dot in the page reference
    let parentPageReference = pageReference.substring(0, pageReference.lastIndexOf('['));
    if (!parentPageReference) {
      parentPageReference = pageReference.substring(0, pageReference.lastIndexOf('.'));
    }
    const parenTarget = parentPageReference.substring(parentPageReference.lastIndexOf('.'));
    parentPageReference = parentPageReference.substring(0, parentPageReference.lastIndexOf('.'));

    if (parentPageReference && parenTarget) {
      createContextEntry(c11nEnv.getContextName(), parenTarget, parentPageReference);
    }
  }

  StateMachineManager.executeStateMachine(
    c11nEnv.getContextName(),
    `${pageReference}${propertyName}`,
    value,
    true,
    options
  );
};

/**
 * This function helps in adding DELETE page instruction for referenced list in context
 * @param index - row index of the
 * @param c11nEnv - pConnect object for the list
 * @param pageRef - pageReference
 * @private
 */
const deleteEntry = (index: number, c11nEnv: C11nEnv, pageRef?: string, options?: { skipStateUpdate?: boolean }) => {
  const { skipStateUpdate = false } = options || {};
  const contextName = c11nEnv.getContextName();
  const referenceList = c11nEnv.getReferenceList();
  const pageReference = pageRef || c11nEnv.getPageReference();
  const deletePageRef = `${pageReference + referenceList}[${index}]`;
  const target = handleTargetInNestedPageList(referenceList);

  clearMessages({
    type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
    pageReference: deletePageRef,
    context: contextName,
    isListEntry: true,
    deletingIndex: index
  });

  createContextEntry(contextName, target, pageReference);
  addPageInstructionReferences(contextName, `${referenceList}[${index}]`);
  const stateMachine = StateMachineManager.getStateMachine(contextName, pageReference, target);
  if (typeof index === 'string') {
    stateMachine.delete(index, skipStateUpdate);
  } else {
    stateMachine.delete(index + 1, skipStateUpdate);
  }
  const unConnectedStateMachines = StateMachineManager.getUnConnectedStateMachines(contextName) || {};
  Object.keys(unConnectedStateMachines).forEach((key) => {
    if (key.includes(`${pageReference}${target}`) && key !== `${pageReference}${target}`) {
      unConnectedStateMachines[key] = null;
    }
  });
};

/**
 * This function helps in adding REORDER page instruction for referenced list in context
 * @param fromIndex - from list index
 * @param toIndex  - to list index
 * @param c11nEnv - pConnect object for the list
 * @private
 */
const reorder = (fromIndex: number, toIndex: number, c11nEnv: C11nEnv) => {
  const contextName = c11nEnv.getContextName();
  const referenceList = c11nEnv.getReferenceList();
  const pageReference = c11nEnv.getPageReference();
  const target = handleTargetInNestedPageList(referenceList);

  createContextEntry(contextName, target, pageReference);
  treemanager.deleteAllListNodes(contextName, `${pageReference}${target}`, false);
  addPageInstructionReferences(contextName, `${referenceList}[${toIndex}]`);
  const stateMachine = StateMachineManager.getStateMachine(contextName, pageReference, target);
  stateMachine.move(fromIndex + 1, toIndex + 1);
  const unConnectedStateMachines = StateMachineManager.getUnConnectedStateMachines(contextName) || {};
  Object.keys(unConnectedStateMachines)?.forEach((key) => {
    if (key.includes(`${pageReference}${target}`) && key !== `${pageReference}${target}`) {
      delete unConnectedStateMachines[key];
    }
  });
};

/**
 * This function checks if input and referenceProp are self reference case
 * @param param property to check
 * @param referenceProp reference to check against
 * @returns true/false based on if self referencing
 * @private
 */
const isSelfReferencedProperty = (param: string, referenceProp: string) => {
  const [, parentPropName] = param.split('.');
  const referencePropParent = referenceProp?.split('.').pop();
  return parentPropName === referencePropParent;
};

/**
 * This function returns the resolved parameter values for content
 * @param parameters datasource parameters
 * @param data data containing the parameter values
 * @param referenceProp reference to check against
 * @returns resolved parameter values
 */
const getParameterValues = (parameters: object, data: object, referenceProp: string) => {
  const values: { [key: string]: any } = {};
  Object.values(parameters).forEach((param) => {
    if (isSelfReferencedProperty(param, referenceProp)) {
      const propNames = Utils.getPropertyName(param);
      const propNamesArr = propNames.substr(propNames.indexOf('.') + 1).split('.');
      if (propNamesArr.length > 1) {
        let resolvedValue = Utils.getIn(data, propNamesArr) ?? '';
        if (!resolvedValue) {
          const str = `!P!${propNamesArr.join(':')}`;
          resolvedValue = Utils.getIn(data, str) ?? '';
        }
        Utils.setIn(values, propNamesArr, resolvedValue);
      } else {
        const resolvedValue = Utils.getIn(data, propNamesArr) ?? '';
        values[propNamesArr[propNamesArr.length - 1]] = resolvedValue;
      }
    }
  });
  return values;
};

/**
 * This function returns the parameters with configured additional properties if any
 * @param additionalProperties configured parameters
 * @param propertyMetadata configured property metadata
 * @param isMultiSelectMode multi-select mode or not
 * @returns returns parameters and additional properties if any.
 * @private
 */
const getAdditionalProperties = (
  additionalProperties: { [key: string]: any },
  propertyMetadata: { [key: string]: any },
  isMultiSelectMode: boolean
) => {
  if (propertyMetadata?.descriptors?.length && !isMultiSelectMode) {
    propertyMetadata.descriptors.forEach((property: string) => {
      const propNames = Utils.getPropertyName(property);
      const propNamesArr = propNames.substr(propNames.indexOf('.') + 1).split('.');
      additionalProperties[propNamesArr.join('.')] = property;
    });
  }
};

/**
 * This function check and clear the required error message(validatemessage prop)
 * @param c11nEnv c11Env object
 * @param fullReference of the field
 * @param required flag to identify if list selection is required
 * @param isMultiSelectMode to check if multiselectmode is enabled
 * @param referencePropName reference prop name
 */
const checkAndClearRequiredErrorMessage = (
  c11nEnv: C11nEnv,
  fullReference: string,
  required: string,
  isMultiSelectMode: boolean,
  referencePropName: string
) => {
  const rowsSelected = c11nEnv.getValue(fullReference) || {};
  if (required && Object.keys(rowsSelected).length > 0) {
    c11nEnv.clearErrorMessages({
      property: isMultiSelectMode ? referencePropName : c11nEnv.getStateProps().value
    });
  }
};

const getReferencePropertyName = (isMultiSelectMode: boolean, pageReference: string, selectionList: string) => {
  if (isMultiSelectMode) {
    return selectionList.substring(1);
  }
  return pageReference.substring(pageReference.lastIndexOf('.') + 1);
};
const getFullPageReference = (isMultiSelectMode: boolean, pageReference: string, selectionList: string) => {
  if (isMultiSelectMode) {
    return `${pageReference}${selectionList}`;
  }
  return pageReference;
};

const getPropertyMetadata = (
  c11nEnv: C11nEnv,
  isMultiSelectMode: boolean,
  referencePropertyName: string,
  contextPageReference: string | null
) => {
  if (isMultiSelectMode) {
    return c11nEnv.getFieldMetadata(`${referencePropertyName}`);
  }
  return c11nEnv.getCurrentPageFieldMetadata(contextPageReference);
};

const isNoContextRendering = (rawViewMetadata?: ComponentMetadataConfig) => {
  return (
    rawViewMetadata?.config?.dataRelationshipContext &&
    rawViewMetadata.type === 'SimpleTableSelect' &&
    rawViewMetadata.config?.selectionMode === 'single'
  );
};

const getPropertyName = (pageReferenceSplit: string[]) => {
  if (pageReferenceSplit.length > 1) {
    return pageReferenceSplit.pop() || '';
  }
  return '';
};

/**
 * updates the redux store on selection of row(s)
 * @param payload - row(s) context object
 * @param c11nEnv - c11nEnv object
 * @param options
 * @private
 */
const setSelectedRows = (payload: GenericObject[], c11nEnv: C11nEnv, options: { skipPageInstructions?: boolean }) => {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  return new Promise<void>((resolve) => {
    const { skipPageInstructions = false } = options;
    const { selectionMode, selectionList, required } = c11nEnv.getConfigProps();
    const isMultiSelectMode = selectionMode === Constants.LIST_SELECTION_MODE.MULTI;
    const contextName = c11nEnv.getContextName();
    let pageReference = c11nEnv.getPageReference();
    let referencePropName = getReferencePropertyName(isMultiSelectMode, pageReference, selectionList);
    const rawViewMetadata = c11nEnv.getRawMetadata();
    let contextPageReference: string | null = null;
    const hasNoContextRendering = isNoContextRendering(rawViewMetadata);
    if (hasNoContextRendering) {
      referencePropName = rawViewMetadata?.config?.dataRelationshipContext as string;
      contextPageReference = pageReference.concat('.').concat(referencePropName);
      pageReference = contextPageReference;
    }
    const fullReference = getFullPageReference(isMultiSelectMode, pageReference, selectionList);
    const propertyMetadata = getPropertyMetadata(c11nEnv, isMultiSelectMode, referencePropName, contextPageReference);

    const {
      datasource: { parameters = {} }
    } = propertyMetadata;
    const additionalProperties = { ...parameters };
    getAdditionalProperties(additionalProperties, propertyMetadata, isMultiSelectMode);

    if (isMultiSelectMode) {
      let target = selectionList;
      target = handleTargetInNestedPageList(target);
      const rows = c11nEnv.getValue(fullReference) || [];
      createContextEntry(c11nEnv.getContextName(), target, pageReference);
      const actions: any[] = [];
      let startIndex = rows.length;
      payload.forEach((row: { [key: string]: any }) => {
        const content = getParameterValues(parameters, row, referencePropName);
        // displayPropertyObject holds displayProperty if it is sent thorugh payload
        // Ex- Multiselect sends display property so that it can be maintained in redux along with unique key
        const displayPropertyObject: { [key: string]: any } = {};
        if (Object.hasOwn(row, 'displayProperty')) {
          const displayProperty = row.displayProperty;
          displayPropertyObject[displayProperty] = row[displayProperty];
        }
        const contentEntries = Object.entries(content);
        let index = rows.findIndex((rowContent: { [key: string]: any }) =>
          contentEntries.every(([param, value]) => rowContent[param] === value)
        );
        if (index === -1) {
          index = startIndex;

          if (row.$selected) {
            startIndex += 1;
          }
        } else {
          startIndex = startIndex ? startIndex - 1 : 0;
        }
        // TODO for list to ensure not to add intructions immediately upon selection.
        // Leverage form handler to prepare page instructions before submit
        if (!skipPageInstructions) {
          const stateMachine = StateMachineManager.getStateMachine(contextName, pageReference, target);
          if (row.$selected) stateMachine.insert(content, index + 1, true, {});
          else stateMachine.delete(index + 1, true);
          /* instructions[contextName][target].content.push({
            instruction: row.$selected ? Constants.LIST_ACTIONS.INSERT : Constants.LIST_ACTIONS.DELETE,
            content,
            target,
            listIndex: index + 1
          }); */
        }

        actions.push({
          instruction: row.$selected ? Constants.LIST_ACTIONS.SELECT : Constants.LIST_ACTIONS.DESELECT,
          listIndex: index,
          content: { ...content, ...displayPropertyObject } // immer will freeze the content obj
        });
      });

      StateManager.dispatch(
        listAction({
          instruction: Constants.LIST_ACTIONS_TYPE.SELECT_DESELECT,
          context: contextName,
          referenceList: `${pageReference}.${referencePropName}`,
          actions
        })
      );
    } else {
      const patchedValues = getParameterValues(additionalProperties, payload[0], referencePropName);
      const compositeKeys = rawViewMetadata?.config?.compositeKeys || [];
      if (c11nEnv.getRawConfigProps().onlyAllowRefreshOnCompositeKeys && compositeKeys.length) {
        const compositeKeyedProperties =
          compositeKeys?.reduce((prev: Record<string, string>, key: string) => {
            const propNames = Utils.getPropertyName(key);
            const propNamesArr = propNames.substr(propNames.indexOf('.') + 1).split('.');
            prev[propNamesArr.join('.')] = key;
            return prev;
          }, {}) || {};
        const compositeKeyedValues = getParameterValues(compositeKeyedProperties, payload[0], referencePropName);
        Object.keys(compositeKeyedValues).forEach((val) => {
          delete patchedValues[val];
          c11nEnv
            .getActionsApi()
            .updateFieldValue(
              `.${val}`,
              compositeKeyedValues[val],
              hasNoContextRendering ? { contextPageReference: pageReference } : {}
            );
          c11nEnv.getActionsApi().triggerFieldChange(`.${referencePropName}.${val}`, compositeKeyedValues[val]);
        });

        const pageReferenceSplit = pageReference.split(/\[\d+\]/gm);
        const propName = getPropertyName(pageReferenceSplit);
        c11nEnv
          .getActionsApi()
          .updateFieldValue(
            propName,
            patchedValues,
            hasNoContextRendering ? { contextPageReference: pageReference } : {}
          );
        c11nEnv.getActionsApi().triggerFieldChange(propName, patchedValues);
      } else {
        /**
         * If pageReference is like caseInfo.content.Employees[0].Manager.Name we must pass propertyName as .Manager.Name
         */
        Object.keys(patchedValues).forEach((val) => {
          c11nEnv
            .getActionsApi()
            .updateFieldValue(
              `.${val}`,
              patchedValues[val],
              hasNoContextRendering ? { contextPageReference: pageReference } : {}
            );
          c11nEnv.getActionsApi().triggerFieldChange(`.${referencePropName}.${val}`, patchedValues[val]);
        });
      }
    }
    checkAndClearRequiredErrorMessage(c11nEnv, fullReference, required, isMultiSelectMode, referencePropName);
    resolve();
  });
};

/**
 * returns the current selected row(s) details
 * @param withPayload - boolean value, if true returns the entire payload
 * @param c11nEnv - c11nEnv Object
 * @returns returns the selected row(s) details
 * @private
 */
const getSelectedRows = (withPayload: boolean, c11nEnv: C11nEnv): Promise<object> => {
  const { selectionMode, selectionList } = c11nEnv.getConfigProps();
  const selectedRows: any[] = [];
  let pageReference = c11nEnv.getPageReference();
  const isMultiSelectMode = selectionMode === Constants.LIST_SELECTION_MODE.MULTI;
  let referencePropName = isMultiSelectMode
    ? selectionList.substring(1)
    : pageReference.substring(pageReference.lastIndexOf('.') + 1);

  const rawViewMetadata = c11nEnv.getRawMetadata();
  let contextPageReference: string | null = null;
  if (
    rawViewMetadata?.config?.dataRelationshipContext &&
    rawViewMetadata.type === 'SimpleTableSelect' &&
    rawViewMetadata.config?.selectionMode === 'single'
  ) {
    referencePropName = rawViewMetadata.config.dataRelationshipContext;
    contextPageReference = pageReference.concat('.').concat(referencePropName);
    pageReference = contextPageReference;
  }
  const fullReference = isMultiSelectMode ? `${pageReference}${selectionList}` : pageReference;
  let referencePropValue = c11nEnv.getValue(fullReference) || [];
  if (!Array.isArray(referencePropValue)) {
    referencePropValue = [referencePropValue];
  }

  if (withPayload) {
    return Promise.resolve(referencePropValue);
  }
  const { datasource: { parameters = {} } = {} } = isMultiSelectMode
    ? c11nEnv.getFieldMetadata(`${referencePropName}`)
    : c11nEnv.getCurrentPageFieldMetadata(contextPageReference);

  referencePropValue.forEach((row: { [key: string]: any }) => {
    const keys = Object.values(parameters).reduce((acc: string, param: any) => {
      const propertyName = param.substring(param.lastIndexOf('.') + 1);
      return isSelfReferencedProperty(param, referencePropName) && row[propertyName]
        ? `${acc}${row[propertyName]}`
        : acc;
    }, '');
    if (keys.trim()) {
      selectedRows.push(keys.trim());
    }
  });

  return Promise.resolve(selectedRows);
};

/**
 * check whether the page-instrcutions dirtyness in the provided context.
 * @param context
 * @returns false if no page-instruction is generated else return true
 * @private
 */
export const isPropertyDirtyPageInstruction = (context: string | null) => {
  if (!context) {
    return false;
  }
  const pageInstructions = getChangedInstructions(context);
  return Array.isArray(pageInstructions) && pageInstructions.length > 0;
};

/**
 * clears the selected row(s) in multiselect list and updates the redux store
 * @param c11nEnv - c11nEnv object
 * @private
 */
function clearSelectedRows(c11nEnv: C11nEnv) {
  const { selectionList, required } = c11nEnv.getConfigProps();
  const contextName = c11nEnv.getContextName();
  const pageReference = c11nEnv.getPageReference();
  const referencePropName = selectionList;

  const fullReference = `${pageReference}${selectionList}`;
  return new Promise<void>((resolve) => {
    let target = referencePropName;
    target = handleTargetInNestedPageList(target);
    createContextEntry(contextName, target, pageReference);

    const stateMachine = StateMachineManager.getStateMachine(contextName, pageReference, target);
    stateMachine.deleteAll();

    checkAndClearRequiredErrorMessage(c11nEnv, fullReference, required, true, referencePropName);
    resolve();
  });
}

/**
 * @param c11nEnv - pConnect object for the list.
 * @param property - reference property of the table
 * @param propertyNames - list of property names.
 * @param uniqueField - unique field for the row
 * @private
 */
export const initDefaultPageInstructions = (
  c11nEnv: C11nEnv,
  property: string,
  propertyNames: string[],
  uniqueField?: string
) => {
  const contextName = c11nEnv.getContextName();
  const pageReference = c11nEnv.getPageReference();
  const target = handleTargetInNestedPageList(property);
  createContextEntry(contextName, target, pageReference);
  const stateMachine = StateMachineManager.getStateMachine(contextName, pageReference, target);
  // if (EnvironmentInfo.getFeatureMap()?.simpleTable?.pageInstructionUUIDGeneration !== false) {
  stateMachine.initPageInstructions(c11nEnv, propertyNames, uniqueField);
  // }
};

/**
 * This deletes all the changes so far we did to the respected state-machine
 * @param c11nEnv - pConnect object for the table
 * @param property - reference property of the table.
 * @private
 */
const deleteAll = (c11nEnv: C11nEnv, property: string, options: { skipStateUpdate?: boolean } = {}) => {
  const { skipStateUpdate = false } = options;
  const pageReference = c11nEnv.getPageReference();
  const target = handleTargetInNestedPageList(property);
  createContextEntry(c11nEnv.getContextName(), property, pageReference);
  const stateMachine = StateMachineManager.getStateMachine(c11nEnv.getContextName(), pageReference, target);
  stateMachine.deleteAll(skipStateUpdate);
};

/**
 * This replaces all the existing properties with the object we sent for reference table.
 * @param c11nEnv - pConnect object for the table
 * @param property - reference property of the table
 * @param payload - payload of the property to which it gets replaced
 * @private
 */
export const replacePage = (
  c11nEnv: C11nEnv,
  property: string,
  payload: object,
  options?: { skipStateUpdate?: boolean }
) => {
  const { skipStateUpdate = false } = options || {};
  const pageReference = c11nEnv.getPageReference();
  const context = c11nEnv.getContextName();
  createContextEntry(context, property, pageReference);
  addPageInstructionReferences(context, Utils.buildInstructionPageReference(pageReference, property));
  const stateMachine = StateMachineManager.getStateMachine(context, pageReference, property);
  stateMachine.replacePage(payload, skipStateUpdate);
};

export const deletePage = (c11nEnv: C11nEnv, property: string, options?: { skipStateUpdate?: boolean }) => {
  const { skipStateUpdate = false } = options || {};
  const pageReference = c11nEnv.getPageReference();
  const context = c11nEnv.getContextName();
  createContextEntry(context, property, pageReference);
  addPageInstructionReferences(context, Utils.buildInstructionPageReference(pageReference, property));
  const stateMachine = StateMachineManager.getStateMachine(context, pageReference, property);
  stateMachine.deletePage(skipStateUpdate);
};

export const generatePageInstructionsForNestedPayload = (
  payload: { [key: string]: any },
  target: string,
  parentC11Env: C11nEnv
) => {
  const iterateAndGenerate = (tPayload: { [key: string]: any }, existingkey: string) => {
    Object.entries(tPayload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const c11nEnv = PCore.createPConnect({
          options: {
            pageReference: parentC11Env.getPageReference(),
            context: parentC11Env.getContextName(),
            referenceList: `${existingkey}.${key}`
          }
        }).getPConnect();
        deleteAll(c11nEnv, `${existingkey}.${key}`, { skipStateUpdate: true });
        for (let i = 0; i < value.length; i += 1) {
          insert(value[i], i, c11nEnv, undefined, {});
        }
      } else if (typeof value === 'object') {
        iterateAndGenerate(value, `${existingkey}.${key}`);
      }
    });
  };
  iterateAndGenerate(payload, target);
};

/**
 * merge page instructions from one context to another context
 * @param fromContext- context name
 * @param toContext- context name
 * @param pageReference- page reference
 * @param skipStateUpdate- flag to skip updating redux state
 * @param targetPage- page to replace when instruction is replace
 * @private
 * @mergeInstructions
 */
export const mergeInstructions = (
  fromContext: string,
  toContext: string,
  pageReference: string,
  skipStateUpdate: boolean,
  targetPage?: string
) => {
  const pageInstructions = getInstructions(fromContext);

  pageInstructions.forEach((instruction) => {
    // For REPLACE with targetPage: route into the parent list SM so the full row path is stored
    // as a node without creating a child SM. All other instructions use their natural target SM.
    const smTarget =
      targetPage && instruction.instruction.toLowerCase() === 'replace'
        ? Utils.convertClipboardPageToPageRefNotation(targetPage)
        : Utils.convertClipboardPageToPageRefNotation(instruction.target);
    if (!StateMachineManager.isStateMachineExist(toContext, pageReference, smTarget)) {
      StateMachineManager.setStateMachine(
        toContext,
        smTarget,
        pageReference,
        new StateMachine(smTarget, pageReference, toContext)
      );
    }
    const stateMachine = StateMachineManager.getStateMachine(toContext, pageReference, smTarget);
    switch (instruction.instruction.toLowerCase()) {
      case 'insert':
        stateMachine.insert(instruction.content, instruction.listIndex, skipStateUpdate, {});
        break;
      case 'update':
        stateMachine.update(instruction.content, instruction.listIndex ?? instruction.groupIndex, skipStateUpdate, {});
        break;
      case 'delete':
        if (instruction.listIndex) {
          stateMachine.delete(instruction.listIndex, skipStateUpdate);
        } else if (instruction.groupIndex) {
          stateMachine.delete(instruction.groupIndex, skipStateUpdate);
        } else {
          stateMachine.deletePage();
        }
        break;
      case 'replace':
        // When targetPage is set, pass the full row path as overrideTarget and the content-root
        // pageReference so replacePage does not double-prefix using the nested SM's own pageReference.
        stateMachine.replacePage(
          instruction.content,
          skipStateUpdate,
          targetPage ? instruction.target : undefined,
          targetPage ? pageReference : undefined
        );
        break;
      case 'move':
        stateMachine.move(instruction.listIndex, instruction.listMoveToIndex);
        break;
      case 'add':
        stateMachine.insert(instruction.content, instruction.groupIndex, skipStateUpdate, {});
        break;
      case 'delete all': {
        stateMachine.deleteAll();
        break;
      }
      /* istanbul ignore next */
      default:
        break;
    }
  });
};

/**
 * This function helps in providing entry point to get list action api's
 * @param c11nEnv - pConnect object for the list
 * @returns returns an object which contains the utility api's for performing list actions.
 * @private
 */
export const listActions = (c11nEnv: C11nEnv): ListActions => {
  return {
    insert: (
      payload: object,
      index: number,
      pageRef?: string,
      options?: { skipStateUpdate?: boolean; insertIfEmpty?: boolean }
    ) => insert(payload, index, c11nEnv, pageRef, options),
    update: (payload: object, index: number, options?: { skipStateUpdate?: boolean; pageRef?: string }) =>
      update(payload, c11nEnv, index, options),
    updateProperty: (
      propertyName: string | undefined,
      value: GenericObject,
      options?: {
        skipStateUpdate?: boolean;
        isArrayDeepMerge?: boolean;
        skipDirtyValidation?: false;
      }
    ) => updateProperty(propertyName, value, c11nEnv, options),
    deleteEntry: (index: number, pageRef?: string, options?: { skipStateUpdate?: boolean }) =>
      deleteEntry(index, c11nEnv, pageRef, options),
    reorder: (fromIndex: number, toIndex: number) => reorder(fromIndex, toIndex, c11nEnv),
    setSelectedRows: (
      rows: GenericObject[],
      options: { skipPageInstructions: boolean } = { skipPageInstructions: false }
    ) => setSelectedRows(rows, c11nEnv, options),
    getSelectedRows: (withPayload = false) => getSelectedRows(withPayload, c11nEnv),
    clearSelectedRows: () => clearSelectedRows(c11nEnv),
    setVisibility: (isVisible: boolean) => setVisibility(c11nEnv, isVisible),
    initDefaultPageInstructions: (property: string, propertyNames: string[], uniqueField?: string) =>
      initDefaultPageInstructions(c11nEnv, property, propertyNames, uniqueField),
    deleteAll: (property: string, options?: { skipStateUpdate?: boolean }) => deleteAll(c11nEnv, property, options),
    replacePage: (property: string, payload: object, options?: { skipStateUpdate?: boolean }) =>
      replacePage(c11nEnv, property, payload, options),
    deletePage: (property: string, options?: { skipStateUpdate?: boolean }) => deletePage(c11nEnv, property, options)
  };
};
